import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { scrubVendorTerms } from "@/lib/vendorTerms";
import type { MessageThinking } from "@/types/chat.type";

type ThoughtSummaryProps = {
  thinking: MessageThinking;
  isLive?: boolean;
};

export function getMessageThinking(
  metadata: Record<string, unknown> | null,
): MessageThinking | null {
  const raw = metadata?.thinking;
  if (!raw || typeof raw !== "object") return null;
  const { text, durationMs } = raw as MessageThinking;
  if (typeof text !== "string" || !text.trim()) return null;
  return {
    text,
    durationMs: typeof durationMs === "number" ? durationMs : 0,
  };
}

export default function ThoughtSummary({
  thinking,
  isLive = false,
}: ThoughtSummaryProps) {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const seconds = Math.max(1, Math.round(thinking.durationMs / 1000));
  const displayText = scrubVendorTerms(thinking.text);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [thinking.text, isLive, expanded]);

  const scrollClass =
    "custom-scrollbar max-h-[160px] overflow-y-auto text-[12px] leading-relaxed text-co-faint whitespace-pre-wrap";

  if (isLive) {
    return (
      <div ref={scrollRef} className={cn(scrollClass, "mb-2")}>
        {displayText}
      </div>
    );
  }

  return (
    <div className="mb-2 min-w-0">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="flex items-center gap-1 text-[11px] text-co-faint transition-colors hover:text-co-muted"
      >
        <ChevronDown
          className={cn(
            "size-3 shrink-0 transition-transform",
            expanded && "rotate-180",
          )}
        />
        Thought for {seconds}s
      </button>
      {expanded ? (
        <div ref={scrollRef} className={cn(scrollClass, "mt-1.5")}>
          {displayText}
        </div>
      ) : null}
    </div>
  );
}
